import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import {
  Gauge, Users, CalendarDays, Receipt, GraduationCap, TrendingUp, Wallet, AlertCircle,
  Cake, FileCheck2, Inbox, MessageSquare, X, ChevronRight, Plus, ArrowUpRight,
  LayoutDashboard, Calendar as CalIcon, Clock as ClockIcon, ShieldAlert,
} from "lucide-react";
import { CalendarTab, TimelineTab, RiskFlagsTab } from "./cockpit_tabs";

/* ─────────── KPI Card ─────────── */
function KpiCard({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const palette = {
    slate: "bg-[#e2e8f0] text-[#475569]",
    amber: "bg-[#fcf3e2] text-[#a38d5b]",
    green: "bg-[#dcfce7] text-[#166534]",
    rose: "bg-[#fee2e2] text-[#9f1239]",
    indigo: "bg-[#e0e7ff] text-[#4338ca]",
  };
  return (
    <div className="sw-card !p-4 flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{label}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${palette[tone] || palette.slate}`}>
          <Icon size={15} />
        </div>
      </div>
      <div className="font-semibold text-[24px] text-[#1e293b] leading-tight tracking-tight">{value}</div>
      {sub && <div className="text-[12px] text-[#64748b]">{sub}</div>}
    </div>
  );
}

/* ─────────── Action queue item ─────────── */
function ActionItem({ item, t }) {
  const urgencyMap = {
    high:   { key: "cockpit.urgency_high",   cls: "bg-[#fef2f2] text-[#991b1b] border-[#fecaca]" },
    normal: { key: "cockpit.urgency_normal", cls: "bg-[#fffbeb] text-[#92400e] border-[#fde68a]" },
    soft:   { key: "cockpit.urgency_soft",   cls: "bg-[#f1f5f9] text-[#475569] border-[#e2e8f0]" },
  };
  const u = urgencyMap[item.urgency] || urgencyMap.normal;
  const iconMap = {
    pto_approval:        CalendarDays,
    reimb_approval:      Receipt,
    loan_approval:       Wallet,
    evaluation_pending:  TrendingUp,
    birthday:            Cake,
  };
  const Icon = iconMap[item.type] || Inbox;
  return (
    <li data-testid={`action-item-${item.type}-${item.item_id}`} className="block group">
      <Link
        to={item.link || "#"}
        className="flex items-start gap-3 p-3 rounded-[12px] border border-[#e2e8f0] hover:border-[#475569] hover:bg-[#f8fafc] transition-colors"
      >
        <div className="w-9 h-9 rounded-[10px] bg-[#f1f5f9] text-[#475569] flex items-center justify-center shrink-0">
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${u.cls}`}>{t(u.key)}</span>
            <div className="font-medium text-[13.5px] text-[#1e293b] truncate">{item.title}</div>
            {item.external && (
              <span className="text-[9.5px] px-1.5 py-0.5 rounded-full bg-[#fcf3e2] text-[#a38d5b] font-semibold uppercase tracking-wide shrink-0">
                {t("ext.provider_badge", { name: item.provider_tenant_name || "" })}
              </span>
            )}
          </div>
          {item.subtitle && <div className="text-[12px] text-[#64748b] mt-0.5 truncate">{item.subtitle}</div>}
        </div>
        <ChevronRight size={16} className="text-[#475569] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
      </Link>
    </li>
  );
}

/* ─────────── Mini org-chart (compact horizontal) ─────────── */
function MiniOrg({ data, onSelect, t }) {
  if (!data) return null;
  const { users, root_id } = data;
  const root = users.find((u) => u.id === root_id);
  if (!root) return null;
  const reports = users.filter((u) => u.manager_id === root_id);
  const initials = (n) => (n || "").split(" ").map((x) => x[0]).slice(0, 2).join("");
  return (
    <div className="sw-card !p-5" data-testid="cockpit-mini-org">
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-3">{t("cockpit.my_team")}</div>
      <div className="flex flex-col items-center gap-3">
        <button
          onClick={() => onSelect(root)}
          className="sw-tree-card is-ceo cursor-pointer !w-auto !min-w-[180px]"
          data-testid="cockpit-mini-org-root"
        >
          <div className="sw-tree-avatar">{initials(root.name)}</div>
          <div className="sw-tree-name">{root.name}</div>
          <div className="sw-tree-title">{root.job_title || "—"}</div>
        </button>
        {reports.length > 0 && (
          <>
            <div className="w-px h-4 bg-[#cbd5e1]" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
              {reports.map((r) => (
                <button
                  key={r.id}
                  onClick={() => onSelect(r)}
                  className="sw-tree-card !w-full !min-w-0"
                  data-testid={`cockpit-mini-org-${r.id}`}
                >
                  <div className="sw-tree-avatar">{initials(r.name)}</div>
                  <div className="sw-tree-name truncate w-full text-center">{r.name}</div>
                  <div className="sw-tree-title truncate w-full text-center">{r.job_title || "—"}</div>
                  {r.external && (
                    <div className="text-[9px] mt-1 px-1.5 py-0.5 rounded-full bg-[#fcf3e2] text-[#a38d5b] font-semibold uppercase tracking-wide">
                      {r.provider_tenant_name || "Ext"}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────── 1:1 modal ─────────── */
function OneOnOneModal({ employee, onClose }) {
  const { t } = useI18n();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [actionItems, setActionItems] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/cockpit/one-on-ones?employee_id=${employee.id}`)
      .then((r) => setHistory(r.data || []))
      .catch(() => {});
  }, [employee.id]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        employee_id: employee.id,
        date,
        notes,
        action_items: actionItems ? actionItems.split("\n").filter(Boolean) : [],
        next_date: nextDate || null,
      };
      await api.post("/cockpit/one-on-ones", body);
      onClose(true);
    } catch (e) {
      alert(e?.response?.data?.detail || t("cockpit.error_saving"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="oneonone-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.1on1")}</div>
            <h3 className="sw-heading text-[20px] mt-1">{employee.name}</h3>
            <div className="text-[12px] text-[#64748b]">{employee.job_title}</div>
          </div>
          <button onClick={() => onClose(false)} className="text-[#94a3b8]"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="sw-label">{t("common.date")}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="sw-input" data-testid="oneonone-date" />
          </div>
          <div>
            <label className="sw-label">{t("cockpit.next_1on1")}</label>
            <input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className="sw-input" data-testid="oneonone-next" />
          </div>
        </div>

        <label className="sw-label">{t("cockpit.notes_label")}</label>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("cockpit.notes_placeholder")}
          className="sw-input"
          data-testid="oneonone-notes"
        />

        <label className="sw-label mt-3">{t("cockpit.action_items")}</label>
        <textarea
          rows={3}
          value={actionItems}
          onChange={(e) => setActionItems(e.target.value)}
          placeholder={t("cockpit.action_items_placeholder")}
          className="sw-input font-mono text-[12.5px]"
          data-testid="oneonone-actions"
        />

        <div className="flex gap-2 mt-4">
          <button onClick={save} disabled={saving} className="sw-btn-primary" data-testid="oneonone-save">
            {saving ? t("cockpit.saving_1on1") : t("cockpit.save_1on1")}
          </button>
          <button onClick={() => onClose(false)} className="sw-btn-secondary">{t("common.cancel")}</button>
        </div>

        {history.length > 0 && (
          <div className="mt-5 border-t border-[#e2e8f0] pt-4">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold mb-3">{t("cockpit.history")}</div>
            <ul className="space-y-2.5">
              {history.slice(0, 5).map((h) => (
                <li key={h.id} className="text-[12.5px] flex gap-3">
                  <span className="text-[#475569] font-medium w-20 shrink-0">{h.date}</span>
                  <div className="flex-1">
                    {h.notes && <div className="text-[#1e293b]">{h.notes}</div>}
                    {h.action_items && h.action_items.length > 0 && (
                      <ul className="text-[#64748b] mt-1 list-disc pl-4">
                        {h.action_items.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    )}
                    {h.next_date && <div className="text-[10.5px] text-[#94a3b8] mt-1">{t("cockpit.next_label")} {h.next_date}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── Team table ─────────── */
function TeamTable({ rows, departments, onOneOnOne, t }) {
  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";
  const balanceColor = (b) => {
    if (b >= 12) return "text-[#166534] bg-[#dcfce7]";
    if (b >= 5) return "text-[#a38d5b] bg-[#fcf3e2]";
    return "text-[#9f1239] bg-[#fee2e2]";
  };
  return (
    <div className="sw-card !p-0 overflow-hidden" data-testid="cockpit-team-table">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.col_employee")}</th>
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.col_dept")}</th>
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.col_pto_balance")}</th>
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.col_next_vacation")}</th>
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.col_last_1on1")}</th>
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.col_last_eval")}</th>
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("cockpit.col_trainings")}</th>
              <th className="px-4 py-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#94a3b8]">{t("cockpit.no_employees")}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]" data-testid={`cockpit-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#1e293b] flex items-center gap-2 flex-wrap">
                    {r.name}
                    {r.external && (
                      <span className="inline-flex px-1.5 py-0.5 rounded-full bg-[#fcf3e2] text-[#a38d5b] text-[9.5px] font-semibold uppercase tracking-wide" data-testid={`team-external-badge-${r.id}`}>
                        {t("ext.provider_badge", { name: r.provider_tenant_name || "" })}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#94a3b8]">{r.job_title || "—"}</div>
                </td>
                <td className="px-4 py-3 text-[#475569]">{deptName(r.department_id)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${balanceColor(r.pto_balance || 0)}`}>
                    {(r.pto_balance || 0).toFixed(1)} d
                  </span>
                </td>
                <td className="px-4 py-3 text-[#475569]">{r.next_vacation || <span className="text-[#cbd5e1]">—</span>}</td>
                <td className="px-4 py-3 text-[#475569]">{r.last_one_on_one || <span className="text-[#cbd5e1]">—</span>}</td>
                <td className="px-4 py-3">
                  {r.last_evaluation ? (
                    <div>
                      <span className="text-[#1e293b] font-medium">{r.last_evaluation.rating ?? "—"}/5</span>
                      <div className="text-[10.5px] text-[#94a3b8]">{r.last_evaluation.date}</div>
                    </div>
                  ) : <span className="text-[#cbd5e1]">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className="text-[#1e293b]">{r.trainings_open}</span>
                  {r.trainings_overdue > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#fee2e2] text-[#9f1239] text-[10px]">
                      <AlertCircle size={10} /> {r.trainings_overdue} {t("cockpit.overdue_short")}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onOneOnOne(r)}
                    className="text-[#475569] hover:text-[#1e293b] inline-flex items-center gap-1 text-[11.5px]"
                    data-testid={`cockpit-row-oneonone-${r.id}`}
                  >
                    <MessageSquare size={13} /> {t("cockpit.1on1")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────── Page ─────────── */
export default function ManagerCockpit() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [scope, setScope] = useState("extended"); // user preference: extended default
  const [tab, setTab] = useState("overview");      // V2: tabs
  const [riskCount, setRiskCount] = useState(0);   // for badge on Riscos tab
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState([]);
  const [team, setTeam] = useState([]);
  const [miniOrg, setMiniOrg] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [oneOnOneFor, setOneOnOneFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, q, tr, mo, d] = await Promise.all([
        api.get(`/cockpit/summary?scope=${scope}`),
        api.get(`/cockpit/action-queue`),
        api.get(`/cockpit/team?scope=${scope}`),
        api.get(`/cockpit/mini-org?scope=${scope}`),
        api.get(`/departments`),
      ]);
      setSummary(s.data);
      setQueue(q.data);
      setTeam(tr.data);
      setMiniOrg(mo.data);
      setDepartments(d.data);
    } catch (e) {
      setError(e?.response?.data?.detail || t("cockpit.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  // Side-load the risk count for the tab badge (whatever tab is active)
  useEffect(() => {
    api.get(`/cockpit/risk-flags?scope=${scope}`)
      .then((r) => setRiskCount((r.data?.counts?.critical || 0) + (r.data?.counts?.warning || 0)))
      .catch(() => setRiskCount(0));
  }, [scope]);

  if (loading && !summary) return <div className="text-[#94a3b8]">{t("common.loading")}</div>;
  if (error) return (
    <div className="sw-card border-rose-200 bg-rose-50 text-rose-900 p-5" data-testid="cockpit-error">
      <div className="font-semibold mb-1">{t("cockpit.unavailable")}</div>
      <div className="text-[13px]">{error}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Gauge size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("cockpit.title")}</h1>
            <p className="text-[13.5px] text-[#747474]">{t("cockpit.greeting", { name: user?.name?.split(" ")[0] || "" })}</p>
          </div>
        </div>
        <div className="inline-flex rounded-[10px] border border-[#cbd5e1] bg-white p-0.5" data-testid="cockpit-scope-toggle">
          <button
            data-testid="cockpit-scope-direct"
            onClick={() => setScope("direct")}
            className={`px-3 py-1.5 text-[12px] rounded-[8px] ${scope === "direct" ? "bg-[#475569] text-white" : "text-[#475569]"}`}
          >{t("cockpit.scope_direct")}</button>
          <button
            data-testid="cockpit-scope-extended"
            onClick={() => setScope("extended")}
            className={`px-3 py-1.5 text-[12px] rounded-[8px] ${scope === "extended" ? "bg-[#475569] text-white" : "text-[#475569]"}`}
          >{t("cockpit.scope_extended")}</button>
        </div>
      </div>

      {/* Tabs bar (V2) */}
      <div className="inline-flex rounded-[10px] border border-[#cbd5e1] bg-white p-0.5 w-fit" data-testid="cockpit-tabs">
        {[
          { key: "overview", label: t("cockpit.tab_overview"), icon: LayoutDashboard },
          { key: "calendar", label: t("cockpit.tab_calendar"), icon: CalIcon },
          { key: "timeline", label: t("cockpit.tab_timeline"), icon: ClockIcon },
          { key: "risks",    label: t("cockpit.tab_risks"),    icon: ShieldAlert, badge: riskCount },
        ].map(({ key, label, icon: TabIcon, badge }) => (
          <button
            key={key}
            data-testid={`cockpit-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-3 py-1.5 text-[12.5px] rounded-[8px] inline-flex items-center gap-1.5 ${
              tab === key ? "bg-[#475569] text-white" : "text-[#475569] hover:bg-[#f1f5f9]"
            }`}
          >
            <TabIcon size={13} />
            {label}
            {badge ? <span className={`text-[10px] rounded-full px-1.5 ${tab === key ? "bg-white/25" : "bg-[#fee2e2] text-[#9f1239]"}`}>{badge}</span> : null}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
      {/* Two-column top: mini-org (1) + KPIs (2 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1">
          <MiniOrg data={miniOrg} onSelect={() => {}} t={t} />
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiCard icon={Users} label={t("cockpit.kpi_headcount")} value={summary?.headcount ?? 0} sub={`${t("cockpit.kpi_scope")}: ${scope === "direct" ? t("cockpit.scope_direct_label") : t("cockpit.scope_extended_label")}`} testid="kpi-headcount" />
          <KpiCard icon={CalendarDays} label={t("cockpit.kpi_pto_planned")} value={`${summary?.pto?.upcoming_90d_count ?? 0}`} sub={t("cockpit.kpi_next_90d", { n: (summary?.pto?.total_balance_days ?? 0).toFixed(1) })} tone="indigo" testid="kpi-pto" />
          <KpiCard icon={TrendingUp} label={t("cockpit.kpi_performance")} value={summary?.performance?.avg_rating ?? "—"} sub={t("cockpit.kpi_evals_90d", { n: summary?.performance?.evaluations_90d ?? 0 })} tone="green" testid="kpi-perf" />
          <KpiCard icon={Receipt} label={t("cockpit.kpi_reimb_ytd")} value={`$${(summary?.reimbursements?.ytd_total ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={t("cockpit.kpi_pending_approval", { n: summary?.reimbursements?.pending_approval ?? 0 })} tone="amber" testid="kpi-reimb" />
          <KpiCard icon={GraduationCap} label={t("cockpit.kpi_trainings")} value={summary?.trainings?.open ?? 0} sub={t("cockpit.kpi_overdue_team", { n: summary?.trainings?.overdue ?? 0 })} tone={summary?.trainings?.overdue ? "rose" : "slate"} testid="kpi-trainings" />
          <KpiCard icon={Wallet} label={t("cockpit.kpi_loans")} value={summary?.loans?.pending_approval ?? 0} sub={t("cockpit.kpi_loans_sub")} testid="kpi-loans" />
        </div>
      </div>

      {/* Action queue */}
      <div className="sw-card !p-5" data-testid="cockpit-action-queue">
        <div className="flex items-center justify-between mb-3">
          <h2 className="sw-heading text-[18px] flex items-center gap-2"><Inbox size={18} /> {t("cockpit.need_now")}</h2>
          <span className="text-[11px] text-[#94a3b8]">{queue.length} {t("cockpit.items")}</span>
        </div>
        {queue.length === 0 ? (
          <div className="text-center py-8 text-[#94a3b8]">
            <FileCheck2 className="mx-auto mb-2 opacity-50" size={28} />
            <div className="text-[13px]">{t("cockpit.all_clear")}</div>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {queue.map((it, i) => <ActionItem key={`${it.type}-${it.item_id}-${i}`} item={it} t={t} />)}
          </ul>
        )}
      </div>

      {/* Team table */}
      <div>
        <h2 className="sw-heading text-[18px] mb-3 flex items-center gap-2"><Users size={18} /> {t("cockpit.people")}</h2>
        <TeamTable rows={team} departments={departments} onOneOnOne={setOneOnOneFor} t={t} />
      </div>
        </>
      )}

      {tab === "calendar" && <CalendarTab scope={scope} />}
      {tab === "timeline" && <TimelineTab scope={scope} />}
      {tab === "risks" && <RiskFlagsTab scope={scope} />}

      {oneOnOneFor && (
        <OneOnOneModal
          employee={oneOnOneFor}
          onClose={(saved) => { setOneOnOneFor(null); if (saved) load(); }}
        />
      )}
    </div>
  );
}
